/**
 * Monthly Performance Table Component
 * Uses the shared DataTable component with the same styling as the Trade Log.
 */

import React, { useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { TradeStatisticsFilters } from "../../../../types/trade";
import { useMonthlyPerformance } from "../../../../hooks/useTradeStatistics";
import { useStatisticsWidgetErrorGate } from "../../../../hooks/useStatisticsWidgetErrorGate";
import { StatisticsInlineError } from "../../../../components/errors/StatisticsInlineError";
import { useDisplayTimeZone } from "../../../../contexts/DisplayTimezoneContext";
import { formatYmBucketLongInZone } from "../../../../utils/displayDateTime";
import { DataTable } from "../../../../components/ui/data-table";
import { StatisticsCardHeadline } from "./statisticsCardHeadline";

interface MonthlyPerformanceTableProps {
  filters?: TradeStatisticsFilters;
}

interface MonthlyRow {
  monthKey: string;
  month: string;
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  winRate: number;
  totalPnL: number;
}

const MonthlyPerformanceTable: React.FC<MonthlyPerformanceTableProps> = ({
  filters,
}) => {
  const { formatMoney } = useDisplayCurrency();
  const displayTimeZone = useDisplayTimeZone();

  const { data, loading, error, errorAction } = useMonthlyPerformance(filters);
  const { showWidgetError, widgetError, widgetAction } = useStatisticsWidgetErrorGate(error, errorAction);

  const monthlyData: MonthlyRow[] = useMemo(() => {
    if (!data || !data.data_points || data.data_points.length === 0) {
      return [];
    }

    return data.data_points
      .map((point) => {
        const monthKey = String(point.month).slice(0, 7);
        const monthName = formatYmBucketLongInZone(monthKey, displayTimeZone);

        return {
          monthKey,
          month: monthName,
          totalTrades: point.total_trades,
          winningTrades: point.winning_trades,
          losingTrades: point.losing_trades,
          winRate: point.win_rate,
          totalPnL: point.total_pnl,
        };
      })
      .sort((a, b) => b.monthKey.localeCompare(a.monthKey));
  }, [data, displayTimeZone]);

  const columns: ColumnDef<MonthlyRow, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "month",
        header: "Month",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-[family-name:var(--font-primary)] text-sm text-gray-400">
            {row.getValue("month")}
          </span>
        ),
      },
      {
        accessorKey: "totalTrades",
        header: "Trades",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-[family-name:var(--font-primary)] text-sm text-gray-400">
            {row.getValue("totalTrades")}
          </span>
        ),
      },
      {
        accessorKey: "winningTrades",
        header: "Wins",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-[family-name:var(--font-primary)] text-sm text-[#06E363]">
            {row.getValue("winningTrades")}
          </span>
        ),
      },
      {
        accessorKey: "losingTrades",
        header: "Losses",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-[family-name:var(--font-primary)] text-sm text-[#FE4244]">
            {row.getValue("losingTrades")}
          </span>
        ),
      },
      {
        accessorKey: "winRate",
        header: "Win Rate",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-[family-name:var(--font-primary)] text-sm text-gray-400">
            {(row.getValue("winRate") as number).toFixed(2)}%
          </span>
        ),
      },
      {
        accessorKey: "totalPnL",
        header: "P&L",
        enableSorting: false,
        cell: ({ row }) => {
          const pnl = row.getValue("totalPnL") as number;
          return (
            <span
              className={`font-[family-name:var(--font-primary)] text-sm ${
                pnl >= 0 ? "text-[#06E363]" : "text-[#FE4244]"
              }`}
            >
              {formatMoney(pnl)}
            </span>
          );
        },
      },
    ],
    [formatMoney],
  );

  if (loading) {
    return (
      <div data-statistics-shadcn-card className="rounded-[6px] border border-border bg-background p-6">
        <StatisticsCardHeadline>Monthly Performance</StatisticsCardHeadline>
        <div className="mt-4 h-[400px] animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (showWidgetError && widgetError) {
    return (
      <div data-statistics-shadcn-card className="rounded-[6px] border border-border bg-background p-6">
        <StatisticsCardHeadline>Monthly Performance</StatisticsCardHeadline>
        <div className="mt-4">
          <StatisticsInlineError message={widgetError} action={widgetAction ?? undefined} />
        </div>
      </div>
    );
  }

  return (
    <div data-statistics-shadcn-card className="rounded-[6px] border border-border bg-background p-4">
      <div className="mb-4">
        <StatisticsCardHeadline>Monthly Performance</StatisticsCardHeadline>
      </div>
      <DataTable
        columns={columns}
        data={monthlyData}
        showPagination={monthlyData.length > 12}
        emptyMessage="No monthly data available."
      />
    </div>
  );
};

export default MonthlyPerformanceTable;
